import { useEffect, useState, useRef } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import ErrorMessage from "../../components/StudioErstellen/ErrorMessage";
import classes from "./CreativeProjectEdit.module.css";
import { useCreateCreativePackage } from "../../hooks/creative/useCreateCreativePackage";
import { useUpdateCreativePackage } from "../../hooks/creative/useUpdateCreativePackage";
import { useCreativePackage } from "../../hooks/creative/useCreativePackages";
import { useDeleteCreativePackage } from "../../hooks/creative/useDeleteCreativePackage";
import { getCloudFrontImageUrl } from "../../util/cloudFrontUtils";
import awsConfig from "../../config/aws-config";
import { StudioArbeitenModal } from "./StudioArbeitenModal";
import { getProjectCoverImage } from "./projectThumbnailUtils";
import { useModalSearchParamHistory } from "../../hooks/useModalSearchParamHistory";
import { saveCreativePackageWithOwnedPortfolioImages } from "./creativePackageMediaFlow";
import {
  getSelectableServiceTypesFromCreative,
  getServiceTypeForCreativePackage,
} from "../../util/creativeTypeUtils";
import {
  GET_PACKAGE_PROJECT_LINKS,
  CREATE_PACKAGE_PROJECT_LINK,
  DELETE_PACKAGE_PROJECT_LINK
} from "../../graphql/mutations/creativeMutations";
import { notifyDeleteError, notifySaveError } from "../../util/notifyActionError";

const SERVICE_TYPE_LABELS = Object.freeze({
  PHOTOGRAPHER: 'Fotografie',
  VIDEOGRAPHER: 'Videografie',
  MUSIC_PRODUCER: 'Musikproduktion',
});

export const CreativePackageEdit = ({ creative, loading, error }) => {
  const navigate = useNavigate();
  const params = useParams();
  const packageId = params.packageId;
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const photoInputRef = useRef(null);
  
  const { handleCreatePackage, loading: createLoading } = useCreateCreativePackage();
  const { handleUpdatePackage, loading: updateLoading } = useUpdateCreativePackage();
  const { handleDeletePackage, loading: deleteLoading } = useDeleteCreativePackage();
  const [selectedArbeitenIds, setSelectedArbeitenIds] = useState([]);

  const {
    isOpen: showStudioArbeitenModal,
    openOverlay: openStudioArbeitenModal,
    closeOverlay: closeStudioArbeitenModal,
  } = useModalSearchParamHistory({
    paramName: "arbeiten",
    paramValue: "open",
    resetKey: packageId || creative?.id,
  });
  const [selectedArbeitenProjects, setSelectedArbeitenProjects] = useState([]);
  const [linksInitialized, setLinksInitialized] = useState(false);
  
  // Fetch package if packageId exists in URL (editing mode)
  const { package: packageData, loading: packageLoading, error: packageError } = useCreativePackage(packageId);

  // Fetch existing package ↔ project links (for edit mode)
  const { data: packageLinksData } = useQuery(GET_PACKAGE_PROJECT_LINKS, {
    skip: !packageId,
    variables: { packageID: packageId }
  });

  // Mutations to create/delete package ↔ project links (refetch so cache stays in sync after update)
  const [createPackageProjectLink] = useMutation(CREATE_PACKAGE_PROJECT_LINK, {
    refetchQueries: ['GetPackageProjectLinks'],
    awaitRefetchQueries: true
  });
  const [deletePackageProjectLink] = useMutation(DELETE_PACKAGE_PROJECT_LINK, {
    refetchQueries: ['GetPackageProjectLinks'],
    awaitRefetchQueries: true
  });

  
  const isEditing = !!packageId && !!packageData;
  
  // Use isActive field instead of status - backend handles status automatically
  // Initialize isActive based on package data or default to true for new packages
  const [isActive, setIsActive] = useState(() => {
    // Use isActive field if available, otherwise check status as fallback
    if (packageData?.isActive !== undefined) {
      return packageData.isActive;
    }
    // Fallback: check status for backward compatibility
    return packageData?.status === 'PUBLISHED' || packageData?.status === 'ACTIVE';
  });

  
  // Form state
  const [formValues, setFormValues] = useState({
    name: '',
    description: '',
    pricePerHour: '',
    deliverables: '',
    includedServices: '',
    deliveryTime: '',
    portfolioImages: []
  });
  
  const [errors, setErrors] = useState({
    name: '',
    description: '',
    pricePerHour: '',
    deliverables: '',
    includedServices: '',
    deliveryTime: '',
    portfolioImages: ''
  });
  const creativeTypeForService = creative?.creativeType;
  const creativeVisualTypesForService = Array.isArray(creative?.visualTypes) ? creative.visualTypes : [];
  const creativeVisualTypesKey = creativeVisualTypesForService.join('|');
  const serviceTypeOptions = getSelectableServiceTypesFromCreative({
    creativeType: creativeTypeForService,
    visualTypes: creativeVisualTypesForService,
  });
  const showServiceTypeSelector = creativeTypeForService === 'VISUAL' && serviceTypeOptions.length > 1;
  const [selectedServiceType, setSelectedServiceType] = useState(() => (
    getServiceTypeForCreativePackage({
      creativeType: creativeTypeForService,
      visualTypes: creativeVisualTypesForService,
    })
  ));

  useEffect(() => {
    setSelectedServiceType((currentServiceType) => (
      getServiceTypeForCreativePackage(
        {
          creativeType: creativeTypeForService,
          visualTypes: creativeVisualTypesForService,
        },
        packageData?.serviceType || currentServiceType
      )
    ));
  }, [creativeTypeForService, creativeVisualTypesKey, packageData?.serviceType]);
  
  const handleStatusChange = (e) => {
    // Use isActive boolean instead of status
    setIsActive(e.target.checked);
  };
  
  // Initialize selected works from existing links when editing
  useEffect(() => {
    if (linksInitialized) return;
    if (!packageId) return;
    if (!packageLinksData) return;
    if (!creative?.projects?.items) return;

    const items = packageLinksData.packageProjectLinksByPackageID?.items || [];
    const linkedIds = items.map((item) => item.projectID);

    setSelectedArbeitenIds(linkedIds);

    const selectedProjects = creative.projects.items.filter((project) =>
      linkedIds.includes(project.id)
    );
    setSelectedArbeitenProjects(selectedProjects);
    setLinksInitialized(true);
  }, [linksInitialized, packageId, packageLinksData, creative]);
  
  // Initialize form values from package if editing
  useEffect(() => {
    if (packageData) {
      // Map existing portfolio images - handle both string and object formats
      const mappedImages = (packageData.portfolioImages || []).map((img, idx) => ({
        ...(typeof img === 'string' ? { url: img } : img),
        order: idx
      }));
      
      // Convert deliverables array to string (use first element or join)
      const deliverablesString = Array.isArray(packageData.deliverables) 
        ? (packageData.deliverables.length > 0 ? packageData.deliverables[0] : '')
        : (packageData.deliverables || '');
      
      // Convert includedServices array to string (use first element or join)
      const includedServicesString = Array.isArray(packageData.includedServices)
        ? (packageData.includedServices.length > 0 ? packageData.includedServices[0] : '')
        : (packageData.includedServices || '');
      
      // Use isActive field if available, otherwise check status as fallback
      const packageIsActive = packageData.isActive !== undefined 
        ? packageData.isActive 
        : (packageData.status === 'PUBLISHED' || packageData.status === 'ACTIVE');
      
      setIsActive(packageIsActive);
      setFormValues({
        name: packageData.name || '',
        description: packageData.description || '',
        pricePerHour: packageData.pricePerHour?.toString() || '',
        deliverables: deliverablesString,
        includedServices: includedServicesString,
        deliveryTime: packageData.deliveryTime || '',
        portfolioImages: mappedImages
      });
    } else {
      // For new packages, default isActive is true
      setIsActive(true);
    }
  }, [packageData]);
  
  // Check if form is valid
  const isFormValid = () => {
    const nameTrimmed = formValues.name?.trim() || '';
    const descriptionTrimmed = formValues.description?.trim() || '';
    const deliverablesTrimmed = formValues.deliverables?.trim() || '';
    const deliveryTimeTrimmed = formValues.deliveryTime?.trim() || '';
    const price = parseFloat(formValues.pricePerHour || 0);
    const requiredImageCount = creative?.creativeType === 'MUSIC' ? 1 : 4;
    
    if (!nameTrimmed || nameTrimmed.length < 20 || nameTrimmed.length > 60) return false;
    if (!descriptionTrimmed || descriptionTrimmed.length < 40 || descriptionTrimmed.length > 500) return false;
    if (!formValues.pricePerHour || price < 5 || price > 10000) return false;
    if (!deliverablesTrimmed || deliverablesTrimmed.length < 10 || deliverablesTrimmed.length > 50) return false;
    if (!deliveryTimeTrimmed || deliveryTimeTrimmed.length < 5 || deliveryTimeTrimmed.length > 30) return false;
    if (!formValues.portfolioImages || formValues.portfolioImages.length !== requiredImageCount) return false;
    return true;
  };
  
  // Check if there are changes (only relevant for editing)
  const hasChanges = () => {
    if (!packageData) return false;
    
    if (formValues.name !== (packageData.name || '')) return true;
    if (formValues.description !== (packageData.description || '')) return true;
    if (parseFloat(formValues.pricePerHour || 0) !== (packageData.pricePerHour || 0)) return true;
    
    // Compare isActive
    const originalIsActive = packageData.isActive !== undefined 
      ? packageData.isActive 
      : (packageData.status === 'PUBLISHED' || packageData.status === 'ACTIVE');
    if (isActive !== originalIsActive) return true;

    const originalServiceType = packageData.serviceType || getServiceTypeForCreativePackage(creative);
    if (selectedServiceType !== originalServiceType) return true;
    
    // Compare deliverables (convert array to string for comparison)
    const originalDeliverables = Array.isArray(packageData.deliverables)
      ? (packageData.deliverables.length > 0 ? packageData.deliverables[0] : '')
      : (packageData.deliverables || '');
    if (formValues.deliverables !== originalDeliverables) return true;
    
    // Compare includedServices (convert array to string for comparison)
    const originalIncludedServices = Array.isArray(packageData.includedServices)
      ? (packageData.includedServices.length > 0 ? packageData.includedServices[0] : '')
      : (packageData.includedServices || '');
    if (formValues.includedServices !== originalIncludedServices) return true;
    
    // Compare deliveryTime
    if (formValues.deliveryTime !== (packageData.deliveryTime || '')) return true;
    
    // Compare portfolio images
    const currentImages = formValues.portfolioImages || [];
    const originalImages = packageData.portfolioImages || [];
    if (currentImages.length !== originalImages.length) return true;

    for (let i = 0; i < currentImages.length; i++) {
      const current = currentImages[i];
      const original = originalImages[i];
      
      // Normalize to objects for comparison
      const currentObj = typeof current === 'string' ? { url: current } : current;
      const originalObj = typeof original === 'string' ? { url: original } : original;
      
      // Compare URL
      if (currentObj?.url !== originalObj?.url) return true;
    }

    // Compare selected works (Ausgewählte Arbeiten)
    // Only relevant when editing an existing package with initialized links
    if (packageId && linksInitialized && packageLinksData) {
      const existingItems =
        packageLinksData.packageProjectLinksByPackageID?.items || [];
      const originalProjectIds = existingItems.map((item) => item.projectID);

      // Sort for stable comparison independent of order
      const sortedOriginalIds = [...originalProjectIds].sort();
      const sortedCurrentIds = [...selectedArbeitenIds].sort();

      if (sortedOriginalIds.length !== sortedCurrentIds.length) {
        return true;
      }

      for (let i = 0; i < sortedOriginalIds.length; i++) {
        if (sortedOriginalIds[i] !== sortedCurrentIds[i]) {
          return true;
        }
      }
    }

    return false;
  };
  
  // Check if any files are currently uploading
  const hasUploadsInProgress = () => {
    if (formValues.portfolioImages) {
      return formValues.portfolioImages.some(img => img.isUploading === true);
    }
    return false;
  };
  
  const handleNameChange = (e) => {
    const value = e.target.value;
    
    if (value.length > 60) {
      return;
    }
    
    setFormValues(prev => ({ ...prev, name: value }));
    
    const trimmed = value.trim();
    if (trimmed && trimmed.length < 15) {
      setErrors(prev => ({ ...prev, name: 'Der Titel muss mindestens 15 Zeichen lang sein.' }));
    } else if (trimmed && trimmed.length > 60) {
      setErrors(prev => ({ ...prev, name: 'Der Titel darf maximal 60 Zeichen lang sein.' }));
    } else if (errors.name) {
      setErrors(prev => ({ ...prev, name: '' }));
    }
  };
  
  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    
    if (value.length > 500) {
      return;
    }
    
    setFormValues(prev => ({ ...prev, description: value }));
    
    const trimmed = value.trim();
    if (!trimmed) {
      setErrors(prev => ({ ...prev, description: 'Du musst eine Beschreibung eingeben.' }));
    } else if (trimmed.length < 40) {
      setErrors(prev => ({ ...prev, description: 'Du musst mindestens 40 Zeichen für deine Beschreibung eingeben.' }));
    } else if (trimmed.length > 500) {
      setErrors(prev => ({ ...prev, description: 'Die Beschreibung darf maximal 500 Zeichen lang sein.' }));
    } else {
      setErrors(prev => {
        if (!prev.description) return prev;
        return { ...prev, description: '' };
      });
    }
  };
  
  const handlePricePerHourChange = (e) => {
    const value = e.target.value;
    
    // Allow only numbers and decimal point
    if (value && !/^\d*\.?\d*$/.test(value)) {
      return;
    }
    
    setFormValues(prev => ({ ...prev, pricePerHour: value }));
    
    // Validate price
    const price = parseFloat(value);
    if (value && (isNaN(price) || price < 5)) {
      setErrors(prev => ({ ...prev, pricePerHour: 'Der Preis muss mindestens 5€ betragen.' }));
    } else if (value && price > 10000) {
      setErrors(prev => ({ ...prev, pricePerHour: 'Der Preis darf 10.000€ nicht überschreiten.' }));
    } else {
      setErrors(prev => {
        if (!prev.pricePerHour) return prev;
        return { ...prev, pricePerHour: '' };
      });
    }
  };
  
  // Handle deliverables textarea change
  const handleDeliverablesChange = (e) => {
    const value = e.target.value;
    
    if (value.length > 50) {
      return;
    }
    
    setFormValues(prev => ({ ...prev, deliverables: value }));
    
    const trimmed = value.trim();
    if (trimmed && trimmed.length < 10) {
      setErrors(prev => ({ ...prev, deliverables: 'Deliverables muss mindestens 10 Zeichen lang sein.' }));
    } else if (trimmed && trimmed.length > 50) {
      setErrors(prev => ({ ...prev, deliverables: 'Deliverables darf maximal 50 Zeichen lang sein.' }));
    } else {
      setErrors(prev => {
        if (!prev.deliverables) return prev;
        return { ...prev, deliverables: '' };
      });
    }
  };
  
  // Handle includedServices textarea change
  const handleIncludedServicesChange = (e) => {
    const value = e.target.value;
    
    if (value.length > 60) {
      return;
    }
    
    setFormValues(prev => ({ ...prev, includedServices: value }));
    
    const trimmed = value.trim();
    if (trimmed && trimmed.length > 60) {
      setErrors(prev => ({ ...prev, includedServices: 'Im Preis enthalten darf maximal 60 Zeichen lang sein.' }));
    } else {
      setErrors(prev => {
        if (!prev.includedServices) return prev;
        return { ...prev, includedServices: '' };
      });
    }
  };
  
  // Handle deliveryTime input change
  const handleDeliveryTimeChange = (e) => {
    const value = e.target.value;
    
    if (value.length > 30) {
      return;
    }
    
    setFormValues(prev => ({ ...prev, deliveryTime: value }));
    
    const trimmed = value.trim();
    if (!trimmed) {
      setErrors(prev => ({ ...prev, deliveryTime: 'Du musst eine Lieferzeit eingeben.' }));
    } else if (trimmed.length < 5) {
      setErrors(prev => ({ ...prev, deliveryTime: 'Lieferung nach Studiobesuch muss mindestens 5 Zeichen lang sein.' }));
    } else if (trimmed.length > 30) {
      setErrors(prev => ({ ...prev, deliveryTime: 'Lieferung nach Studiobesuch darf maximal 30 Zeichen lang sein.' }));
    } else {
      setErrors(prev => {
        if (!prev.deliveryTime) return prev;
        return { ...prev, deliveryTime: '' };
      });
    }
  };
  
  // Handle file selection (input change)
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) {
      return;
    }
    
    // Check if adding these files would exceed max allowed
    const maxImages = creative?.creativeType === 'MUSIC' ? 1 : 4;
    const currentCount = formValues.portfolioImages.length;
    if (currentCount + files.length > maxImages) {
      setErrors(prev => ({ 
        ...prev, 
        portfolioImages: creative?.creativeType === 'MUSIC'
          ? 'Bitte füge genau 1 Cover-Bild hinzu.'
          : 'Bitte füge genau 4 Fotos hinzu.' 
      }));
      if (e.target) {
        e.target.value = '';
      }
      return;
    }
    
    // Create media items for photos
    const mediaItems = files.map((file) => ({
      type: 'photo',
      file: file,
      previewUrl: URL.createObjectURL(file),
      size: file.size,
      uploadProgress: 0,
      isUploading: true
    }));
    
    const startIndex = formValues.portfolioImages?.length || 0;
    
    setFormValues(prev => ({
      ...prev,
      portfolioImages: [...(prev.portfolioImages || []), ...mediaItems]
    }));
    
    setTimeout(() => {
      handleAddMedia(mediaItems, startIndex);
    }, 0);
    
    if (e.target) {
      e.target.value = '';
    }
  };

  // Handle files dropped via drag & drop
  const handleFileDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const files = Array.from(event.dataTransfer?.files || []);

    if (!files.length) {
      return;
    }

    const maxImages = creative?.creativeType === 'MUSIC' ? 1 : 4;
    const currentCount = formValues.portfolioImages.length;
    if (currentCount + files.length > maxImages) {
      setErrors(prev => ({
        ...prev,
        portfolioImages: creative?.creativeType === 'MUSIC'
          ? 'Bitte füge genau 1 Cover-Bild hinzu.'
          : 'Bitte füge genau 4 Fotos hinzu.'
      }));
      return;
    }

    const pseudoEvent = { target: { files } };
    await handleFileSelect(pseudoEvent);
  };
  
  // Handle media items (photos only)
  const handleAddMedia = (newMediaItems, providedStartIndex = null) => {
    let startIndex = providedStartIndex;
    if (startIndex === null) {
      startIndex = formValues.portfolioImages.length - newMediaItems.length;
    }

    const processedItems = newMediaItems.map((item, idx) => ({
      ...item,
      type: 'photo',
      url: item.previewUrl,
      order: startIndex + idx,
      isUploading: false,
      uploadProgress: 100
    }));

    setFormValues(prev => {
      const newImages = [...(prev.portfolioImages || [])];

      processedItems.forEach((processedItem) => {
        const itemIndex = newImages.findIndex((img) =>
          img.file === processedItem.file && img.isUploading
        );

        if (itemIndex >= 0 && itemIndex < newImages.length) {
          newImages[itemIndex] = processedItem;
        }
      });

      return {
        ...prev,
        portfolioImages: newImages
      };
    });

    if (errors.portfolioImages) {
      setErrors(prev => ({ ...prev, portfolioImages: '' }));
    }
  };

  const handleRemoveImage = (index) => {
    const item = formValues.portfolioImages[index];

    if (item?.previewUrl && item.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(item.previewUrl);
    }
    if (item?.thumbnailUrl && item.thumbnailUrl.startsWith('blob:')) {
      URL.revokeObjectURL(item.thumbnailUrl);
    }
    
    setFormValues(prev => {
      const newImages = prev.portfolioImages.filter((_, i) => i !== index);
      const reorderedImages = newImages.map((img, idx) => ({
        ...img,
        order: idx
      }));
      return {
        ...prev,
        portfolioImages: reorderedImages
      };
    });
    
    if (formValues.portfolioImages.length > 1) {
      setErrors(prev => ({ ...prev, portfolioImages: '' }));
    }
  };
  
  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };
  
  const handleDragLeave = () => {
    setDragOverIndex(null);
  };
  
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    
    setFormValues(prev => {
      const images = [...prev.portfolioImages];
      const draggedItem = images[draggedIndex];
      
      images.splice(draggedIndex, 1);
      images.splice(dropIndex, 0, draggedItem);
      
      const reorderedImages = images.map((img, idx) => ({
        ...img,
        order: idx
      }));
      
      return {
        ...prev,
        portfolioImages: reorderedImages
      };
    });
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };
  
  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };
  
  // Validation function
  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      name: '',
      description: '',
      pricePerHour: '',
      deliverables: '',
      includedServices: '',
      deliveryTime: '',
      portfolioImages: ''
    };
    
    const nameTrimmed = formValues.name?.trim() || '';
    if (!nameTrimmed) {
      newErrors.name = "Du musst einen Titel eingeben.";
      isValid = false;
    } else if (nameTrimmed.length < 3) {
      newErrors.name = "Der Titel muss mindestens 3 Zeichen lang sein.";
      isValid = false;
    } else if (nameTrimmed.length > 60) {
      newErrors.name = "Der Titel darf maximal 60 Zeichen lang sein.";
      isValid = false;
    }
    
    const descriptionTrimmed = formValues.description?.trim() || '';
    if (!descriptionTrimmed) {
      newErrors.description = "Du musst eine Beschreibung eingeben.";
      isValid = false;
    } else if (descriptionTrimmed.length < 40) {
      newErrors.description = "Du musst mindestens 40 Zeichen für deine Beschreibung eingeben.";
      isValid = false;
    } else if (descriptionTrimmed.length > 500) {
      newErrors.description = "Die Beschreibung darf maximal 500 Zeichen lang sein.";
      isValid = false;
    }
    
    const price = parseFloat(formValues.pricePerHour || 0);
    if (!formValues.pricePerHour || price < 5) {
      newErrors.pricePerHour = "Der Preis muss mindestens 5€ betragen.";
      isValid = false;
    } else if (price > 10000) {
      newErrors.pricePerHour = "Der Preis darf 10.000€ nicht überschreiten.";
      isValid = false;
    }
    
    const deliverablesTrimmed = formValues.deliverables?.trim() || '';
    if (!deliverablesTrimmed) {
      newErrors.deliverables = "Du musst Deliverables eingeben.";
      isValid = false;
    } else if (deliverablesTrimmed.length < 10) {
      newErrors.deliverables = "Deliverables muss mindestens 10 Zeichen lang sein.";
      isValid = false;
    } else if (deliverablesTrimmed.length > 50) {
      newErrors.deliverables = "Deliverables darf maximal 50 Zeichen lang sein.";
      isValid = false;
    }
    
    const includedServicesTrimmed = formValues.includedServices?.trim() || '';
    if (includedServicesTrimmed && includedServicesTrimmed.length > 60) {
      newErrors.includedServices = "Im Preis enthalten darf maximal 60 Zeichen lang sein.";
      isValid = false;
    }
    
    const deliveryTimeTrimmed = formValues.deliveryTime?.trim() || '';
    if (!deliveryTimeTrimmed) {
      newErrors.deliveryTime = "Du musst eine Lieferzeit eingeben.";
      isValid = false;
    } else if (deliveryTimeTrimmed.length < 5) {
      newErrors.deliveryTime = "Lieferung nach Studiobesuch muss mindestens 5 Zeichen lang sein.";
      isValid = false;
    } else if (deliveryTimeTrimmed.length > 30) {
      newErrors.deliveryTime = "Lieferung nach Studiobesuch darf maximal 30 Zeichen lang sein.";
      isValid = false;
    }

    const requiredImageCount = creative?.creativeType === 'MUSIC' ? 1 : 4;
    if (!formValues.portfolioImages || formValues.portfolioImages.length !== requiredImageCount) {
      newErrors.portfolioImages = creative?.creativeType === 'MUSIC'
        ? "Bitte füge genau 1 Cover-Bild hinzu."
        : "Bitte füge genau 4 Fotos hinzu.";
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };
  
  // Handle delete button click
  const handleDelete = async () => {
    if (!packageData?.id) return;

    const confirmed = window.confirm('Möchtest du dieses Paket wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.');
    if (!confirmed) return;

    try {
      await handleDeletePackage({ id: packageData.id });
      // Navigate back to overview
      navigate(`/creative-editing/${creative.id}`);
    } catch (error) {
      notifyDeleteError(error, { onRetry: () => handleDelete() });
    }
  };

  // Determine if package should be PUBLISHED (all required fields are complete)
  const shouldBePublished = () => {
    // Check if form is valid (all required fields are filled correctly)
    return isFormValid();
  };

  // Sync selected works with backend PackageProjectLink join table
  const syncPackageProjectLinks = async (currentPackageId) => {
    if (!currentPackageId) return;

    const existingItems =
      packageLinksData?.packageProjectLinksByPackageID?.items || [];
    const existingProjectIds = existingItems.map((item) => item.projectID);

    const toCreate = selectedArbeitenIds.filter(
      (id) => !existingProjectIds.includes(id)
    );
    const toDelete = existingItems.filter(
      (item) => !selectedArbeitenIds.includes(item.projectID)
    );

    await Promise.all([
      ...toCreate.map((projectID) =>
        createPackageProjectLink({
          variables: { input: { packageID: currentPackageId, projectID } }
        })
      ),
      ...toDelete.map((link) =>
        deletePackageProjectLink({
          variables: { input: { id: link.id } }
        })
      )
    ]);
  };

  // Handle save button click
  const handleSave = async () => {
    if (hasUploadsInProgress()) {
      return;
    }

    if (!validateForm()) {
      return;
    }
    
    if (isEditing && !hasChanges()) {
      return;
    }
    
    try {
      const serviceType = getServiceTypeForCreativePackage(creative, selectedServiceType);
      
      const packageType = isEditing && packageData?.packageType 
        ? packageData.packageType 
        : 'BASIC'; // Based on the title "Basic-Paket hinzufügen"
      
      // Convert deliverables string to array (schema expects array)
      const deliverablesArray = formValues.deliverables?.trim() 
        ? [formValues.deliverables.trim()] 
        : [];
      
      // Convert includedServices string to array (schema expects array)
      const includedServicesArray = formValues.includedServices?.trim()
        ? [formValues.includedServices.trim()]
        : [];
      
      // Use isActive field - backend handles status automatically
      // Note: We still need to set status for backward compatibility, but isActive is the primary field
      // Backend will automatically update creative status based on isActive changes
      
      await saveCreativePackageWithOwnedPortfolioImages({
        creative,
        isEditing,
        packageId,
        packageData,
        portfolioImages: formValues.portfolioImages,
        handleCreatePackage,
        handleUpdatePackage,
        syncPackageProjectLinks,
        buildPackageData: ({ packageId: resolvedPackageId, portfolioImages }) => {
          const packageDataToSave = {
            id: resolvedPackageId,
            creativeID: creative.id,
            serviceType: serviceType,
            packageType: packageType,
            name: formValues.name.trim(),
            description: formValues.description.trim(),
            pricePerHour: parseFloat(formValues.pricePerHour),
            deliverables: deliverablesArray,
            includedServices: includedServicesArray,
            deliveryTime: formValues.deliveryTime?.trim() || null,
            portfolioImages,
            duration: isEditing && packageData?.duration ? packageData.duration : 1, // Preserve existing or default
            price: parseFloat(formValues.pricePerHour), // Use pricePerHour as price
            isActive: isActive, // Primary field - backend automatically updates creative status based on this
            // Note: status field is kept for GraphQL schema compatibility, but backend prioritizes isActive
            // Backend automatically sets status and updates creative.canBePublished when isActive changes
            status: isActive ? 'PUBLISHED' : 'DRAFT'
          };

          // Preserve other fields when editing
          if (isEditing) {
            // Preserve fields that aren't in the form (don't overwrite form values)
            // Note: includedServices and deliveryTime are already set from form state above
            if (packageData.equipment) {
              packageDataToSave.equipment = packageData.equipment;
            }
            if (packageData.isPopular !== undefined) {
              packageDataToSave.isPopular = packageData.isPopular;
            }
            if (packageData.order !== undefined) {
              packageDataToSave.order = packageData.order;
            }
          }

          return packageDataToSave;
        },
      });
      
      navigate(`/creative-editing/${creative.id}/overview`, { state: { saved: true } });
    } catch (error) {
      if (error.cleanupError) {
        console.warn('Creative-Package-Portfolio-Cleanup nach fehlgeschlagenem Package-Save ist fehlgeschlagen:', error.cleanupError);
      }
      notifySaveError(error, { onRetry: () => handleSave() });
    }
  };

  // Payout preview disabled – use calculateHostPayout from platformFees when re-enabled
  // const handlePayoutChange = (price) => calculateHostPayout(price);

  const handleBack = () => {
    navigate(-1);
  };

  const handleStudioArbeitenSelectionSave = (projectIds) => {
    setSelectedArbeitenIds(projectIds);

    if (creative?.projects?.items) {
      const selectedProjects = creative.projects.items.filter((project) =>
        projectIds.includes(project.id)
      );
      setSelectedArbeitenProjects(selectedProjects);
    } else {
      setSelectedArbeitenProjects([]);
    }
  };

  const handleRemoveSelectedArbeit = (projectId) => {
    const updatedIds = selectedArbeitenIds.filter((id) => id !== projectId);
    handleStudioArbeitenSelectionSave(updatedIds);
  };
  
  // Show loading if creative or package is loading
  if (loading || (packageId && packageLoading)) {
    return (
      <div className={classes.creativeProfileEditPage}>
        <div className={classes.contentContainer}>
          <header className={classes.skeletonHeader}>
            <div className={classes.skeletonBackButton}>
              <div className={classes.skeletonBackIcon}></div>
              <div className={classes.skeletonBackText}></div>
            </div>
            <div className={classes.skeletonSaveButton}></div>
          </header>
          <div className={classes.mainContent}>
            <div className={classes.textContainer}>
              <div className={classes.skeletonTitle}></div>
              <div className={classes.skeletonDescription} style={{ width: '70%', height: '60px', marginTop: '8px' }}></div>
            </div>
            <div className={classes.inputsContainer}>
              <div className={classes.titleInputContainer}>
                <div className={classes.skeletonInput} style={{ height: '24px', width: '80px', marginBottom: '12px' }}></div>
                <div className={classes.skeletonInput}></div>
              </div>
              <div className={classes.descriptionInputContainer}>
                <div className={classes.skeletonInput} style={{ height: '24px', width: '120px', marginBottom: '12px' }}></div>
                <div className={classes.skeletonDescription}></div>
              </div>
              <div className={classes.titleInputContainer}>
                <div className={classes.skeletonInput} style={{ height: '24px', width: '140px', marginBottom: '12px' }}></div>
                <div className={classes.skeletonInput}></div>
              </div>
              <div className={classes.artistContainer}>
                <div className={classes.skeletonInput} style={{ height: '24px', width: '200px', marginBottom: '12px' }}></div>
                <div className={classes.skeletonInput} style={{ height: '80px' }}></div>
              </div>
              <div className={classes.fotoMediaContainer}>
                <div className={classes.skeletonInput} style={{ height: '24px', width: '180px', marginBottom: '12px' }}></div>
                <div className={classes.skeletonMediaGrid}>
                  {[...Array(4)].map((_, index) => (
                    <div key={index} className={classes.skeletonMediaItem}></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Show error if creative or package has error
  if (error || (packageId && packageError)) {
    return (
      <div className={classes.contentContainer}>
        <div className={classes.errorContainer}>
          <p>Creative-Pakete konnten nicht geladen werden.</p>
        </div>
      </div>
    );
  }
  
  if (!creative) {
    return (
      <div className={classes.contentContainer}>
        <div className={classes.errorContainer}>
          <p>Creative-Profil wurde nicht gefunden.</p>
        </div>
      </div>
    );
  }
  
  // If editing but package not found
  if (packageId && !packageData) {
    return (
      <div className={classes.contentContainer}>
        <div className={classes.errorContainer}>
          <p>Paket wurde nicht gefunden.</p>
        </div>
      </div>
    );
  }
  
  const isLoading = createLoading || updateLoading;
  const isNoChangeEdit = isEditing && isFormValid() && !hasChanges();
  const isSaveDisabled = isLoading || hasUploadsInProgress() || isNoChangeEdit;
  
  return (
    <div className={classes.creativeProfileEditPage}>
      <div className={classes.contentContainer}>
        <header className={classes.headerContainer}>
          <div className={classes.header}>
          <button onClick={handleBack} className={classes.backButtonPhone}>
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
            </svg>
          </button>
          <button onClick={handleBack} className={classes.backButton}>
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
              <path d="M6.33301 0.0712891L7.15039 0.888672L7.22168 0.958984L1.88281 6.29688L7.18652 12.041L7.25391 12.1143L7.18066 12.1826L6.33105 12.9668L6.25781 13.0342L6.18945 12.9609L0.0771484 6.33984L0.078125 6.33887L0.0712891 6.33301L0 6.2627L6.2627 0L6.33301 0.0712891Z" fill="#1F1F1F"/>
            </svg>
            <p className="semiboldSmall">Zurück</p>
          </button>
          <button 
            className={`${classes.saveButton} ${!isSaveDisabled ? classes.saveButtonActive : ''} mediumLarge`}
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            {isLoading ? 'Speichern...' : (isEditing ? 'Änderungen speichern' : 'Paket hinzufügen')}
          </button>
          </div>
        </header>
        
        <div className={classes.mainContent}>
          <div className={classes.textContainer}>
            <h3 className="titleSemibold">{isEditing ? 'Bearbeite dein Paket' : 'Füge ein Paket hinzu.'}</h3>
            {isEditing && (
            <p className="slimRegularLargeText" style={{color: "var(--dark-grey-4)"}}>
                Bearbeite dein Paket und aktualisiere die Informationen.
            </p>
            )}
          </div>
          
          {/* Blue Info Box */}
          <div className={classes.videoFotoInfoBox} style={{ 
            borderRadius: '12px',
            background: '#FFF3EC',
            border: '0px'
          }}>
            <span className="mediumLarge">💡Wichtig zu wissen:</span>
            <p>
            Der User zahlt nur für die Zeit, die ihr gemeinsam im Studio verbringt. Wenn du Vor- oder Nachbereitung hast, solltest du das in deinen Stundenpreis einrechnen, damit sich der Job für dich lohnt.
            </p>
          </div>
          
          <div className={classes.inputsContainer}>
            {/* Status Input - Only show when editing */}
            {isEditing && (
              <div className={classes.activateContainer}>
                <header className={classes.activateHeader}>
                  <div className={classes.activateTitle}>
                    <h4 className="semiboldMiddle">Paket Status</h4>
                  </div>
                  <div className={classes.switchContainer}>
                      <label className={classes.switch}>
                          <input type="checkbox" checked={isActive} onChange={handleStatusChange} />
                          <span className={classes.slider}></span>
                      </label>
                  </div>
                </header>
              </div>
            )}
            {/* Title Input */}
            {showServiceTypeSelector && (
              <div className={classes.titleInputContainer}>
                <label className="semiboldMiddle">Paket-Typ</label>
                <select
                  className={`${classes.addUserInfosInputText} regularMiddle`}
                  value={selectedServiceType}
                  onChange={(event) => setSelectedServiceType(event.target.value)}
                >
                  {serviceTypeOptions.map((serviceType) => (
                    <option key={serviceType} value={serviceType}>
                      {SERVICE_TYPE_LABELS[serviceType] || serviceType}
                    </option>
                  ))}
                </select>
                <p className="slimRegularSmallText">
                  Wähle, ob dieses Paket für Foto oder Video gilt.
                </p>
              </div>
            )}

            <div className={classes.titleInputContainer}>
              <label className="semiboldMiddle">Titel</label>
              <input 
                type="text" 
                className={`${classes.addUserInfosInputText} regularMiddle ${errors.name ? classes.inputError : ''}`} 
                placeholder= {creative?.creativeType === 'MUSIC' ? 'Beispiel: Recording-Session' : 'Beispiel: Portrait Foto Session'} 
                value={formValues.name || ''} 
                onChange={handleNameChange}
                maxLength={60}
              />
              {/* <p className="slimRegularSmallText">Mach im Titel klar, dass dieses Paket nur das enthält, was im Studio entsteht. </p> */}
              <div className={classes.inputFooter}>
                {errors.name && (
                  <ErrorMessage>{errors.name}</ErrorMessage>
                )}
                <div className={`${classes.textCounter} slimRegularSmallText`}>
                  {(formValues.name?.length || 0)} / 60
                </div>
              </div>
            </div>
            
            {/* Description Input */}
            <div className={classes.descriptionInputContainer}>
              <label className="semiboldMiddle">Beschreibung</label>
              <textarea 
                className={`${classes.descriptionTextarea} regularMiddle ${errors.description ? classes.inputError : ''}`} 
                placeholder= {creative?.creativeType === 'MUSIC' ? 'Beispiel: Professionelle Studio-Session für deinen Song oder dein Musikprojekt…' : 'Beispiel: Professionelles Porträtshooting für dich, deine Brand oder dein Team...'}
                value={formValues.description || ''} 
                onChange={handleDescriptionChange}
                maxLength={140}
              />
              <div className={classes.inputFooter}>
                {errors.description && (
                  <ErrorMessage>{errors.description}</ErrorMessage>
                )}
                <div className={`${classes.textCounter} slimRegularSmallText`}>
                  {(formValues.description?.length || 0)} / 140
                </div>
              </div>
            </div>
            
            {/* Price Per Hour Input */}
            <div className={classes.titleInputContainer}>
              <label className="semiboldMiddle">Preis pro Stunde</label>
              <div style={{ position: 'relative', width: '100%' }}>
                <input 
                  type="text" 
                  className={`${classes.addUserInfosInputText} regularMiddle ${errors.pricePerHour ? classes.inputError : ''}`} 
                  placeholder="Beispiel: 100"
                  value={formValues.pricePerHour || ''} 
                  onChange={handlePricePerHourChange}
                  style={{ paddingRight: '40px' }}
                />
                <div className={classes.inputPriceSuffix}>
                  <span className="regularMiddle">€</span>
                </div>
              </div>
              {/* <p className="slimRegularSmallText">User zahlen die Studiozeit mit dir (Vor -und Nachbearbeitung im Preis inklusive)<br/> Kalkuliere also die Bearbeitung in deinen Preis mit.</p> */}
              {/* <p>Dein Payout: {handlePayoutChange(formValues?.pricePerHour || 0)}€/h</p> */}
              
              <p className="slimRegularSmallText">User zahlen die Studiozeit mit dir (Vor- und Nachbearbeitung im Preis inklusive)<br/> Kalkuliere also die Bearbeitung in deinen Preis mit.</p>
              
              <div className={classes.inputFooter}>
                {errors.pricePerHour && (
                  <ErrorMessage>{errors.pricePerHour}</ErrorMessage>
                )}
              </div>
            </div>
            
            {/* Deliverables Textarea */}
            <div className={classes.descriptionInputContainer}>
              <label className="semiboldMiddle">Deliverables pro Stunde</label>
              <textarea 
                className={`${classes.descriptionTextarea} regularMiddle ${errors.deliverables ? classes.inputError : ''}`} 
                placeholder= {creative?.creativeType === 'MUSIC' ? 'Beispiel: Aufnahme & Basis-Mix' : 'Beispiel: 15 - 20 bearbeitete Fotos'}
                value={formValues.deliverables || ''} 
                onChange={handleDeliverablesChange}
                maxLength={50}
              />
              <div className={classes.inputFooter}>
                {errors.deliverables && (
                  <ErrorMessage>{errors.deliverables}</ErrorMessage>
                )}
                <div className={`${classes.textCounter} slimRegularSmallText`}>
                  {(formValues.deliverables?.length || 0)} / 50
                </div>
              </div>
            </div>
            
            {/* Im Preis enthalten Textarea */}
            <div className={classes.descriptionInputContainer}>
              <label className="semiboldMiddle">Im Preis enthalten</label>
              <textarea 
                className={`${classes.descriptionTextarea} regularMiddle ${errors.includedServices ? classes.inputError : ''}`} 
                placeholder= {creative?.creativeType === 'MUSIC' ? 'Beispiel: Studiozeit, Aufnahme & Basis-Mix' : 'Beispiel: Vorbereitung, Fotoshoot & professionelle Retusche'}
                value={formValues.includedServices || ''} 
                onChange={handleIncludedServicesChange}
                maxLength={60}
              />
              <div className={classes.inputFooter}>
                {errors.includedServices && (
                  <ErrorMessage>{errors.includedServices}</ErrorMessage>
                )}
                <div className={`${classes.textCounter} slimRegularSmallText`}>
                  {(formValues.includedServices?.length || 0)} / 60
                </div>
              </div>
            </div>
            
            {/* Lieferung nach Studiobesuch Input */}
            <div className={classes.titleInputContainer}>
              <label className="semiboldMiddle">Lieferung nach Studiobesuch</label>
              <input 
                type="text" 
                className={`${classes.addUserInfosInputText} regularMiddle ${errors.deliveryTime ? classes.inputError : ''}`} 
                placeholder= {creative?.creativeType === 'MUSIC' ? 'Beispiel: Innerhalb von 1 Woche' : 'Beispiel: Vorbereitung, Fotoshoot & professionelle Retusche'}
                value={formValues.deliveryTime || ''} 
                onChange={handleDeliveryTimeChange}
                maxLength={30}
              />
              <div className={classes.inputFooter}>
                {errors.deliveryTime && (
                  <ErrorMessage>{errors.deliveryTime}</ErrorMessage>
                )}
                <div className={`${classes.textCounter} slimRegularSmallText`}>
                  {(formValues.deliveryTime?.length || 0)} / 30
                </div>
              </div>
            </div>

            {/* Ausgewählte Arbeiten anzeigen */}
            {selectedArbeitenProjects.length > 0 && (
              <div className={classes.arbeitenSelectedContainer}>
                {selectedArbeitenProjects.map((project) => {
                  const coverImage = getProjectCoverImage(project);
                  const audioData = project?.audios?.[0];

                  const durationMs = audioData?.durationMs;
                  const duration =
                    durationMs != null
                      ? `${Math.floor(durationMs / 60000)}:${String(
                          Math.floor((durationMs % 60000) / 1000)
                        ).padStart(2, "0")}`
                      : "";

                  return (
                    <div
                      key={project.id}
                      className={classes.arbeitenSelectedItem}
                    >
                      <div className={classes.arbeitenSelectedImage}>
                        {coverImage && (
                          <img src={coverImage} alt={project.title} />
                        )}
                      </div>
                      <div className={classes.arbeitenSelectedText}>
                        <p className="semiboldSmall">{project.title}</p>
                      </div>
                      <button
                        type="button"
                        className={classes.arbeitenSelectedRemoveButton}
                        onClick={() => handleRemoveSelectedArbeit(project.id)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path fillRule="evenodd" clipRule="evenodd" d="M5.3008 6L0 0.699201L0.699201 0L6 5.3008L11.3008 0L12 0.699201L6.6992 6L12 11.3008L11.3008 12L6 6.6992L0.699201 12L0 11.3008L5.3008 6Z" fill="white"/>
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Arbeiten mit Service verknüpfen */}
            {/* <div
              className={classes.studioArbeitenModalInput}
              onClick={() => openStudioArbeitenModal()}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M16.0469 8.58248H0.046875V7.50617H16.0469V8.58248Z" fill="#1F1F1F"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M8.58121 0.0507812V16.0508H7.50489V0.0507812H8.58121Z" fill="#1F1F1F"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M16.0469 8.58248H0.046875V7.50617H16.0469V8.58248Z" stroke="black" strokeWidth="0.1" strokeMiterlimit="10"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M8.58121 0.0507812V16.0508H7.50489V0.0507812H8.58121Z" stroke="black" strokeWidth="0.1" strokeMiterlimit="10"/>
              </svg>
              <span className="slimRegularLargeText">Arbeitsbeispiele hinzufügen</span>
            </div> */}
            
            {/* Portfolio Images */}
            <div
              className={classes.fotoMediaContainer}
              onDragOver={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onDrop={handleFileDrop}
            >
              <label className="semiboldMiddle">
              {creative?.creativeType === 'MUSIC' 
                ? 'Cover-Bild hochladen'
                : 'Fotos'
              }
              </label>
              <button 
                className={classes.uploadButton}
                onClick={() => {
                  const maxImages = creative?.creativeType === 'MUSIC' ? 1 : 4;
                  if (formValues.portfolioImages.length < maxImages && photoInputRef.current) {
                    photoInputRef.current.click();
                  }
                }}
                type="button"
                disabled={formValues.portfolioImages.length >= (creative?.creativeType === 'MUSIC' ? 1 : 4)}
                style={{ opacity: formValues.portfolioImages.length >= (creative?.creativeType === 'MUSIC' ? 1 : 4) ? 0.5 : 1 }}
              >
                <span className={`regularMiddle ${classes.textButtonSecondary}`}>Drag & Drop oder Hochladen</span>
                <span className={`mediumLarge ${classes.textButton}`}>{creative?.creativeType === 'MUSIC' ? 'Foto hinzufügen' : 'Fotos hinzufügen'} </span>
              </button>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <p className="slimRegularSmallText" style={{color: "var(--dark-grey-4)", marginTop: '-8px'}}>
                {creative?.creativeType === 'MUSIC' 
                  ? 'Bitte füge ein Cover-Bild hinzu, das den Service zeigt.'
                  : 'Bitte füge genau 4 Fotos hinzu.'
                }
              </p>
              {errors.portfolioImages && (
                <ErrorMessage>{errors.portfolioImages}</ErrorMessage>
              )}
              {formValues.portfolioImages.length > 0 && (
                <div className={classes.mediaPreview}>
                  {formValues.portfolioImages.map((item, index) => {
                    let displayUrl = '';
      
                    const imageUrl = item.url;
                    if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
                      try {
                        displayUrl = getCloudFrontImageUrl(imageUrl, {
                          width: 300,
                          height: 300,
                          fit: 'cover'
                        });
                      } catch (error) {
                        try {
                          const cleanKey = imageUrl.startsWith('public/') ? imageUrl : `public/${imageUrl}`;
                          displayUrl = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
                        } catch (fallbackError) {
                          displayUrl = imageUrl;
                        }
                      }
                    } else {
                      displayUrl = imageUrl;
                    }
                    
                    const isUploading = item.isUploading === true;
                    const uploadProgress = item.uploadProgress || 0;
                    const hasError = uploadProgress === -1;
                    
                    return (
                      <div 
                        key={index} 
                        className={`${classes.mediaItem} ${draggedIndex === index ? classes.dragging : ''} ${dragOverIndex === index ? classes.dragOver : ''} ${isUploading ? classes.uploading : ''}`}
                        draggable={!isUploading}
                        onDragStart={(e) => !isUploading && handleDragStart(e, index)}
                        onDragOver={(e) => !isUploading && handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => !isUploading && handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                      >
                        {displayUrl ? (
                          <img src={displayUrl} alt={`Preview ${index + 1}`} />
                        ) : null}
                        {isUploading && (
                          <div className={classes.uploadProgressOverlay}>
                            <div className={classes.circularProgress}>
                              <svg className={classes.progressSvg} viewBox="0 0 36 36">
                                <circle
                                  className={classes.progressCircleBg}
                                  cx="18"
                                  cy="18"
                                  r="16"
                                  fill="none"
                                />
                                <circle
                                  className={classes.progressCircle}
                                  cx="18"
                                  cy="18"
                                  r="16"
                                  fill="none"
                                  strokeDasharray={`${uploadProgress * 100.53 / 100}, 100.53`}
                                />
                              </svg>
                            </div>
                          </div>
                        )}
                        {hasError && (
                          <div className={classes.uploadErrorOverlay}>
                            <span>⚠️</span>
                          </div>
                        )}
                        <button 
                          className={classes.removeButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage(index);
                          }}
                          type="button"
                          title={isUploading ? "Upload abbrechen" : "Entfernen"}
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {isEditing && (
                <div className={classes.deleteButtonContainer}>
                  <button 
                    className={classes.deleteButton}
                    onClick={handleDelete}
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? 'Löschen...' : 'Paket löschen'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <StudioArbeitenModal
        creative={creative}
        open={showStudioArbeitenModal}
        onClose={closeStudioArbeitenModal}
        selectedProjectIds={selectedArbeitenIds}
        onSaveSelection={handleStudioArbeitenSelectionSave}
      />
    </div>
  );
};

export default CreativePackageEdit;
